/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderPermission,
  ContactType,
  SalesFolderShortDto,
  SalesFolderStatus,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { ActionFlow, AppRouter, EnumDictionary, getRouteInitData } from '@gh/common-shared';
import { AutocompleteLoadMoreEvent, DialogService, FormMode, NotificationService } from '@gh/core-ui';
import { ContactListItem, JobDPPBusinessService, JobDppDetailsGeneral } from '../../..';
import { JobDppGeneralTabFormState } from './job-dpp-details-general-tab-form.service';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  ContactDetailsDialogConfig,
  contactDetailsDialogRoute,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { ID } from '@gh/common-shared-data';
import { JobDppDetailsFormState } from '../job-dpp-details-container-form.service';
import { Observable } from 'rxjs';
import { filter$, map$ } from '@gh/rx/operators';
import { TranslationService } from '@gh/core-mls';

@Component({
  selector: 'gh-job-dpp-details-general-tap',
  template: require('./job-dpp-details-general-tab.component.html'),
             viewProviders: [
               JobDppDetailsFormState,
               JobDppGeneralTabFormState,
             ],
})
export class JobDppDetailsGeneralTabComponent extends AbstractFormComponent<JobDppDetailsGeneral>implements OnInit {
  statusDict: EnumDictionary<SalesFolderStatus>;
  notProceedingReasons: any[];
  details: JobDppDetailsGeneral;
  jobId: ID;
  salesFolders: SalesFolderShortDto[] = [];

  get canOpenSalesFolder(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Master) ||
      this.userContext.hasSystemRole(SystemRoleBit.Corporate) ||
      this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT) ||
      this.userContext.hasPermission(BuilderPermission.Sales.Leads.VIEW);
  }

  get statusFieldValue(): string {
    let result = this.statusDict.get(this.form.get('salesFolderStatus')?.value);
    if (this.form.get('salesFolderStatus')?.value === SalesFolderStatus.WillNotComplete
      && this.form.get('salesFolderNotProceedingReason')?.value) {
      // tslint:disable-next-line:prefer-template
      result += ' (' + this.notProceedingReasons.find((item) => `${item.id}`
        === `${this.form.get('salesFolderNotProceedingReason')?.value}`)?.name + ')';
    }
    return result;
  }

  get isEditOrView(): boolean {
    return this.form.mode === FormMode.ViewMode || this.form.mode === FormMode.EditMode;
  }

  get havePermissions(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.VIEW);
  }

  constructor(private route: ActivatedRoute,
              public form: JobDppGeneralTabFormState,
              private router: AppRouter,
              private userContext: BuilderUserContext,
              private notifications: NotificationService,
              private dialogs: DialogService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private businessService: JobDPPBusinessService,
              private navigationService: BuilderNavigationService,
              private translationService: TranslationService,
              private business: JobDPPBusinessService) {
    super();
  }

  ngOnInit(): void {
    const { statusDict, notProceedingReasons, jobId } = getRouteInitData(this.route);
    this.details = this.businessService.jobDetails;
    this.jobId = jobId;
    this.notProceedingReasons = notProceedingReasons;
    this.statusDict = statusDict;
    this.form.build(this.details);
  }

  get completeFlow(): ActionFlow<FormFlowState<JobDppDetailsGeneral>> {
    return this.pipelines.complete(
      {
        execute: this.commonActions.execute((state) => this.businessService.update({...state.value, id: this.jobId })),
        finish: (state: any) => {
          const { value } = state;

          if (this.form.createMode) {
            this.router.switchTo(['/job-admin/jobs/dpp', value.id]);
          } else {
            this.form.build(value);
          }
        },
      });
  }

  loadMoreJobAdmins(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findAllJobAdminsByName(event.query));
  }

  onJobAdminSelect(): void {
    this.form.setJobAdminActive(true);
  }

  goToAel(): void {
    this.navigationService.goToAel({
                                     entityId: this.jobId,
                                     entityType: 'JOB',
                                     label: 'lbl_job',
                                   });
  }

  onDocsOpen(): void {
    this.businessService.openDocs();
  }

  onOpenSpec(): void {
    this.businessService.openSpec(this.form.dirty);
  }

  loadContacts($event: AutocompleteLoadMoreEvent, types?: ContactType[]): void {
    $event.load(this.businessService.getContacts($event.query, types));
  }

  onCreateCounty(): void {
    this.openCounty();
  }

  onOpenCounty(): void {
    this.openCounty(this.form.getCounty());
  }

  onSalesFolderChange(): void {
  }

  onOpenSalesFolderInfo(): void {
    this.router.navigateToChild([`sales/leads`, this.details.contactId], {
        parentTitle: `${this.translationService.get('lbl_job')}: #${this.details.jobNumber}` ,
        queryParams: {
          salesFolderId: this.form.getControl('salesFolderId').value,
        },
    });
  }

  private openCounty(openedCounty?: ContactListItem): void {
    this.openContact(ContactType.CityCounty, openedCounty)
      .subscribe((county) => this.form.setCounty(county));
  }

  private openContact(contactType: ContactType, openedContact?: ContactListItem): Observable<ContactListItem> {
    return this.dialogs.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      contactType,
      contactId: openedContact ? openedContact.id : void 0,
      parentTitle: this.translationService.get(
        'lbl_job_number_with_param',
        { number: this.details.jobNumber },
      ),
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(({ contactId, name }) => (<ContactListItem>{ id: contactId, name })));
  }
}
